import { computed } from 'vue';
import { normalizeTileAuth } from '@/utils/tileAuth';

const DEFAULT_THEME = 'Default';

const windowOrNull = () => (typeof window === 'undefined' ? null : window);

const catalogOrEmpty = () => windowOrNull()?.catalog ?? {};

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

const normalizeSettings = (settings) => {
    return settings && typeof settings === 'object' && !Array.isArray(settings) ? settings : {};
};

const resolveIsSuperAdmin = (context) => {
    if (typeof context.isSuperAdmin === 'boolean') {
        return context.isSuperAdmin;
    }

    const user = context.user;

    // Only trust the client-side tenant_id when it is explicitly present on the
    // serialized user; otherwise fail closed so super-admin-only UI stays hidden.
    if (user && Object.prototype.hasOwnProperty.call(user, 'tenant_id')) {
        return user.tenant_id === null || user.tenant_id === undefined;
    }

    return false;
};

export function normalizeFeatureKey(feature) {
    return String(feature).startsWith('feature.') ? String(feature) : `feature.${feature}`;
}

export function settingToBoolean(value, defaultValue = false) {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value === 1;
    }

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();

        if (['1', 'true', 'yes', 'on'].includes(normalized)) {
            return true;
        }

        if (['0', 'false', 'no', 'off', ''].includes(normalized)) {
            return false;
        }
    }

    return defaultValue;
}

export function getTenantContext() {
    const catalog = catalogOrEmpty();
    const tenant = catalog.tenant ?? {};

    return {
        id: firstDefined(catalog.tenant_id, tenant.id, catalog.user?.tenant_id, null),
        name: firstDefined(catalog.name, catalog.tenant_name, tenant.name, null),
        theme: firstDefined(catalog.theme, catalog.tenant_theme, tenant.theme, DEFAULT_THEME),
        settings: { ...normalizeSettings(firstDefined(catalog.settings, catalog.tenant_settings, tenant.settings_map, tenant.settings, {})), ...normalizeSettings(catalog.tile_catalog?.settings) },
        permissionsEnabled: settingToBoolean(firstDefined(catalog.tenant_permissions, tenant.has_permissions, false)),
        user: firstDefined(catalog.user, null),
        isSuperAdmin: typeof catalog.is_super_admin === 'boolean' ? catalog.is_super_admin : undefined,
        tileAuth: normalizeTileAuth(catalog.tile_auth),
        tileCatalog: catalog.tile_catalog ?? null,
        browserStoragePrefix: firstDefined(catalog.browser_storage_prefix, 'catalog'),
        layout: firstDefined(catalog.layout, null),
        // Optional browser services of the tenant (config/browser_services.php).
        browserServices: firstDefined(catalog.browser_services, null),
    };
}

export function getTenantTileUrl(layer, index = 0) {
    return catalogOrEmpty().tile_catalog?.layers?.[layer]?.[index] ?? '';
}

export function getTenantTilePath(url) {
    return catalogOrEmpty().tile_catalog?.paths?.[url] ?? url;
}

export function getTenantSetting(key, defaultValue = null) {
    const settings = getTenantContext().settings;

    return Object.prototype.hasOwnProperty.call(settings, key) ? settings[key] : defaultValue;
}

export function isFeatureEnabled(feature, defaultValue = false) {
    return settingToBoolean(getTenantSetting(normalizeFeatureKey(feature), defaultValue), defaultValue);
}

export function setTenantTileAuth(tileAuth) {
    const catalog = windowOrNull()?.catalog;
    const normalized = normalizeTileAuth(tileAuth);

    if (catalog) {
        catalog.tile_auth = normalized;
    }

    return normalized;
}

export function setTenantLayout(layout) {
    const catalog = windowOrNull()?.catalog;

    if (catalog) {
        catalog.layout = layout;
    }

    return layout;
}

export function setTenantIdentity(user = {}, isSuperAdmin = false) {
    const catalog = windowOrNull()?.catalog;

    if (catalog) {
        catalog.user = user;
        catalog.is_super_admin = isSuperAdmin;
    }
}

export function useTenant() {
    const context = computed(() => getTenantContext());

    return {
        context,
        settings: computed(() => context.value.settings),
        theme: computed(() => context.value.theme),
        user: computed(() => context.value.user),
        isAdmin: computed(() => resolveIsSuperAdmin(context.value)),
        setting: getTenantSetting,
        feature: isFeatureEnabled,
        featureRef: (feature, defaultValue = false) => computed(() => isFeatureEnabled(feature, defaultValue)),
    };
}

export function useFeature(feature, defaultValue = false) {
    return computed(() => isFeatureEnabled(feature, defaultValue));
}

export function setTenantTileCatalog(tileCatalog) {
    const catalog = windowOrNull()?.catalog;

    if (catalog) {
        catalog.tile_catalog = tileCatalog;
    }

    return tileCatalog;
}
